import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreatePhysicianGroupMutation } from "../../../../services/ConfigurationService";

interface AddPhysicianGroupDialogProps {
  company: string;
}

const AddPhysicianGroupDialog: React.FC<AddPhysicianGroupDialogProps> = ({ company }) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createdId, setCreatedId] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    contactEmail: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createGroup, { isLoading }] = useCreatePhysicianGroupMutation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setForm({ name: "", phone: "", contactEmail: "", street: "", city: "", state: "", zip: "" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      const result = await createGroup({ ...form, company }).unwrap();
      setCreatedId(result.id);
      setOpen(false);
      setConfirmOpen(true);
      setForm({ name: "", phone: "", contactEmail: "", street: "", city: "", state: "", zip: "" });
    } catch (err) {
      setErrorMessage("Failed to create physician group.");
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(createdId);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Group
      </Button>

      {/* Create Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Physician Group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
            <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
            <TextField label="Contact Email" name="contactEmail" value={form.contactEmail} onChange={handleChange} fullWidth />
            <TextField label="Street" name="street" value={form.street} onChange={handleChange} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth />
              <TextField label="State" name="state" value={form.state} onChange={handleChange} sx={{ width: 100 }} />
              <TextField label="Zip" name="zip" value={form.zip} onChange={handleChange} sx={{ width: 120 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !form.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog with Practice ID */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Group Created</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Physician group created successfully.
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, bgcolor: "#f1f5f9", borderRadius: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
              {createdId}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="caption" sx={{ mt: 1, color: "#64748b" }}>
            Use this Practice ID when configuring CIAM portal access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setConfirmOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export { AddPhysicianGroupDialog };
